'use client'

import { TextEntry } from '@repo/ui/TextEntry'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useState } from 'react'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import PropTypes from 'prop-types'

export const NewMatterNumberEntry = ({ onChange, onError, clientNo, size = 'small', sx = {}, testMode = false }) => {
  const { fetchNextMatterNo, checkIfMatterNoExists } = useMattersStore()
  const { fetchUserCanOverrideMatterNo, userCanOverrideMatterNo } = useLookupsStore()
  const [matterNo, setMatterNo] = useState('')
  const [error, setError] = useState(null)
  const debouncedMatterNo = useDebounce(matterNo, 300)
  const [fetchingNextMatterNo, setFetchingNextMatterNo] = useState(false)

  useEffect(() => {
    if (userCanOverrideMatterNo === null && !testMode) {
      fetchUserCanOverrideMatterNo()
    }
  }, [userCanOverrideMatterNo, fetchUserCanOverrideMatterNo, testMode])

  useEffect(() => {
    setMatterNo('')
    setError('')

    if (!clientNo || fetchingNextMatterNo) {
      return
    }

    const getNextClientNo = async () => {
      setFetchingNextMatterNo(true)
      const matterNo = await fetchNextMatterNo({ clientNo })
      if (matterNo) {
        setMatterNo(matterNo)
        setFetchingNextMatterNo(false)
      }
    }

    if (!testMode) getNextClientNo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientNo])

  useEffect(() => {
    if (!userCanOverrideMatterNo || !debouncedMatterNo || !clientNo) return

    const checkIfMatterNoIsUnique = async () => {
      const exists = await checkIfMatterNoExists({ clientNo, matterNo: debouncedMatterNo })
      const errorMessage = exists ? 'Matter no already exists' : null
      setError(errorMessage)
      if (onError) onError(errorMessage)
    }

    checkIfMatterNoIsUnique()
  }, [debouncedMatterNo, clientNo, userCanOverrideMatterNo, checkIfMatterNoExists, onError])

  const handleChange = (newValue) => {
    if (userCanOverrideMatterNo) setMatterNo(newValue)
  }

  useEffect(() => {
    if (onChange) {
      onChange(matterNo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterNo])

  return (
    <div data-testid="NewMatterNumberEntry" style={{ ...sx }}>
      <TextEntry
        name="matterNumber"
        onChange={handleChange}
        value={matterNo}
        disabled={userCanOverrideMatterNo && !clientNo}
        required
        isNumber
        min={1}
        label="Matter no"
        readOnly={!userCanOverrideMatterNo}
        errors={error ? { matterNumber: error } : null}
        size={size}
        sx={{ width: '100%' }}
      />
    </div>
  )
}

NewMatterNumberEntry.propTypes = {
  onChange: PropTypes.func,
  onError: PropTypes.func,
  clientNo: PropTypes.string,
  size: PropTypes.string,
  sx: PropTypes.object,
  testMode: PropTypes.bool
}
